import { useEffect, useMemo, useState } from "react";
import { api, type Board as BoardData, type Task } from "../api.ts";
import { tokens, useTheme } from "../glassTheme.tsx";
import { GlassFloat } from "../components/glass/Glass.tsx";
import { Segment } from "../components/glass/Segment.tsx";
import { Strip } from "../components/board/Strip.tsx";
import { ValueList } from "../components/board/ValueList.tsx";
import { BoardRow } from "../components/board/BoardRow.tsx";

type Tab = "all" | "by-value";

/** Musts first, then dated ascending, then the board's own sort field (§2). */
function boardSort(a: Task, b: Task): number {
  const am = a.tier === "must" ? 0 : 1;
  const bm = b.tier === "must" ? 0 : 1;
  if (am !== bm) return am - bm;
  const ad = a.due ?? "9999-99-99";
  const bd = b.due ?? "9999-99-99";
  if (ad !== bd) return ad.localeCompare(bd);
  return a.sort - b.sort;
}

export function Board({
  board, onOpen, onChanged, valueFilter, onValueFilterChange,
}: {
  board: BoardData; onOpen: (id: string) => void; onChanged: () => void;
  valueFilter: string | null; onValueFilterChange: (v: string | null) => void;
}) {
  const { colors } = useTheme();
  const [tab, setTab] = useState<Tab>("all");
  const [toast, setToast] = useState<{ label: string; undo: () => void } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const titleById = useMemo(() => new Map(board.tasks.map((t) => [t.id, t.title])), [board.tasks]);
  const afterTitleFor = (t: Task) => (t.blocked_by[0] ? titleById.get(t.blocked_by[0]) ?? null : null);

  const rows = useMemo(() => {
    let list = board.tasks.filter((t) => t.state === "open" || t.state === "waiting");
    if (tab === "by-value" && valueFilter) list = list.filter((t) => t.value_id === valueFilter);
    return [...list].sort(boardSort);
  }, [board.tasks, tab, valueFilter]);

  const doRow = async (t: Task, patch: Record<string, unknown>, undoPatch: Record<string, unknown>, label: string) => {
    try {
      await api.update(t.id, patch);
      onChanged();
      setToast({
        label,
        undo: async () => { try { await api.update(t.id, undoPatch); onChanged(); } catch { /* best-effort */ } setToast(null); },
      });
    } catch { /* the row simply doesn't move; no separate error surface for a swipe */ }
  };

  return (
    <div style={{ padding: "12px 16px 0" }}>
      <div style={{ marginBottom: 14, fontFamily: tokens.font.serif, fontSize: tokens.type.screenTitle.size, color: colors.ink }}>
        Board
      </div>

      <Strip board={board} />

      <div style={{ marginTop: 16 }}>
        <Segment
          options={[{ key: "all", label: "All" }, { key: "by-value", label: "By value" }]}
          value={tab}
          onChange={(v) => { setTab(v); if (v === "all") onValueFilterChange(null); }}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === "by-value" && !valueFilter && (
          <ValueList values={board.values} onSelect={onValueFilterChange} />
        )}

        {(tab === "all" || (tab === "by-value" && valueFilter)) && (
          <div>
            {tab === "by-value" && valueFilter && (
              <button
                onClick={() => onValueFilterChange(null)}
                style={{
                  display: "block", background: "none", border: "none", padding: "0 0 8px", cursor: "pointer",
                  fontFamily: tokens.font.sans, fontSize: 13, color: colors.ink2,
                }}
              >‹ {board.values.find((v) => v.key === valueFilter)?.name}</button>
            )}
            <div style={{ background: colors.sheet, borderRadius: tokens.radius.card, padding: "0 12px" }}>
              {rows.map((t) => (
                <BoardRow
                  key={t.id} task={t} afterTitle={afterTitleFor(t)} onOpen={() => onOpen(t.id)}
                  onDone={() => doRow(t, { state: "done" }, { state: t.state }, "Done")}
                  onPark={(because) => doRow(
                    t, { state: "parked", parked_because: because }, { state: t.state, parked_because: t.parked_because }, "Parked",
                  )}
                />
              ))}
              {rows.length === 0 && (
                <p style={{ margin: 0, padding: "14px 4px", fontSize: 13, color: colors.ink2 }}>Nothing here.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 96, display: "flex", justifyContent: "center", zIndex: 32, pointerEvents: "none" }}>
          <GlassFloat style={{ pointerEvents: "auto", padding: "10px 16px", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontFamily: tokens.font.sans, fontSize: 13.5, color: colors.ink }}>{toast.label}</span>
            <button
              onClick={toast.undo}
              style={{
                fontFamily: tokens.font.sans, fontSize: 13.5, color: colors.ink2, background: "none",
                border: "none", padding: 0, cursor: "pointer", textDecoration: "underline",
              }}
            >undo</button>
          </GlassFloat>
        </div>
      )}
    </div>
  );
}
